import { Dialog, Switch, Tab, Transition } from '@headlessui/react';
import {
  ArrowPathIcon,
  BellIcon,
  CameraIcon,
  CheckCircleIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  KeyIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';
import PhoneInput from '../../components/ui/PhoneInput';
import PasswordInput from '../../components/ui/PasswordInput';
import { User } from '../../types';

interface ProfileUpdateData {
  first_name?: string;
  last_name?: string;
  email?: string;
  password?: string;
  phone?: string;
  timezone?: string;
  avatar_url?: string;
}

// Built-in avatar options
const BUILT_IN_AVATARS = [
  { id: 'avatar-1', emoji: '👤', bg: 'bg-gray-500' },
  { id: 'avatar-2', emoji: '🧑‍💼', bg: 'bg-blue-500' },
  { id: 'avatar-3', emoji: '👨‍💼', bg: 'bg-indigo-500' },
  { id: 'avatar-4', emoji: '👩‍💼', bg: 'bg-purple-500' },
  { id: 'avatar-5', emoji: '🧑‍🔧', bg: 'bg-orange-500' },
  { id: 'avatar-6', emoji: '👨‍🔧', bg: 'bg-amber-500' },
  { id: 'avatar-7', emoji: '🚗', bg: 'bg-red-500' },
  { id: 'avatar-8', emoji: '🏎️', bg: 'bg-rose-500' },
  { id: 'avatar-9', emoji: '🚙', bg: 'bg-green-500' },
  { id: 'avatar-10', emoji: '🏁', bg: 'bg-teal-500' },
  { id: 'avatar-11', emoji: '⭐', bg: 'bg-yellow-500' },
  { id: 'avatar-12', emoji: '💼', bg: 'bg-slate-500' },
];

const GRADIENT_AVATARS = [
  { id: 'grad-1', initials: true, bg: 'bg-gradient-to-br from-blue-500 to-purple-600' },
  { id: 'grad-2', initials: true, bg: 'bg-gradient-to-br from-green-400 to-blue-500' },
  { id: 'grad-3', initials: true, bg: 'bg-gradient-to-br from-pink-500 to-orange-400' },
  { id: 'grad-4', initials: true, bg: 'bg-gradient-to-br from-purple-500 to-pink-500' },
  { id: 'grad-5', initials: true, bg: 'bg-gradient-to-br from-yellow-400 to-red-500' },
  { id: 'grad-6', initials: true, bg: 'bg-gradient-to-br from-indigo-500 to-cyan-400' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const safeGetItem = (key: string) => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // no-op
  }
};

const safeRemoveItem = (key: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // no-op
  }
};

const parseStoredBoolean = (value: string | null, fallback: boolean) => {
  if (value === null) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const ProfilePage: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // UI State
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState<'authenticator' | 'sms' | null>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(() => {
    const savedAvatar = safeGetItem('userAvatar');
    return savedAvatar;
  });
  const [uploadedImage, setUploadedImage] = useState<string | null>(() => {
    const savedImage = safeGetItem('userAvatarImage');
    return savedImage;
  });

  // 2FA State
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(() => {
    const saved = safeGetItem('emailNotifications');
    return parseStoredBoolean(saved, true);
  });
  const [smsNotifications, setSmsNotifications] = useState(() => {
    const saved = safeGetItem('smsNotifications');
    return parseStoredBoolean(saved, false);
  });
  const [dealAlerts, setDealAlerts] = useState(() => {
    const saved = safeGetItem('dealAlerts');
    return parseStoredBoolean(saved, true);
  });
  const [inventoryAlerts, setInventoryAlerts] = useState(() => {
    const saved = safeGetItem('inventoryAlerts');
    return parseStoredBoolean(saved, true);
  });
  const [paymentReminders, setPaymentReminders] = useState(() => {
    const saved = safeGetItem('paymentReminders');
    return parseStoredBoolean(saved, true);
  });

  // Display preferences
  const [selectedTheme, setSelectedTheme] = useState<'light' | 'dark' | 'system'>(() => {
    const savedTheme = safeGetItem('theme') as 'light' | 'dark' | 'system' | null;
    return savedTheme || 'light';
  });
  const [timezone, setTimezone] = useState(() => {
    const saved = safeGetItem('timezone');
    return saved || 'America/New_York';
  });
  const [showCurrencySymbols, setShowCurrencySymbols] = useState(() => {
    const saved = safeGetItem('showCurrencySymbols');
    return parseStoredBoolean(saved, true);
  });
  const [use12HourFormat, setUse12HourFormat] = useState(() => {
    const saved = safeGetItem('use12HourFormat');
    return parseStoredBoolean(saved, true);
  });
  const [compactTableView, setCompactTableView] = useState(() => {
    const saved = safeGetItem('compactTableView');
    return parseStoredBoolean(saved, false);
  });

  // Apply theme changes
  useEffect(() => {
    const applyTheme = (theme: 'light' | 'dark' | 'system') => {
      const root = document.documentElement;

      if (theme === 'system') {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      } else if (theme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }

      // Save to localStorage
      safeSetItem('theme', theme);
    };

    applyTheme(selectedTheme);

    // Listen for system theme changes if system is selected
    if (selectedTheme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('system');
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [selectedTheme]);

  // Fetch current user profile
  const {
    data: profile,
    isLoading,
    error,
    refetch,
  } = useQuery<User>({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const response = await apiService.get<User>('/users/me');
      return response;
    },
    onSuccess: (data) => {
      setFirstName(data.first_name || '');
      setLastName(data.last_name || '');
      setEmail(data.email || '');
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileUpdateData) => {
      return await apiService.put<User>('/users/me', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    },
    onError: (error: unknown) => {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      toast.error(detail || 'Failed to update profile');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: { password: string }) => {
      return await apiService.put<User>('/users/me', data);
    },
    onSuccess: () => {
      toast.success('Password changed successfully!');
      setShowPasswordSection(false);
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error: unknown) => {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      toast.error(detail || 'Failed to change password');
    },
  });

  const handleSaveProfile = () => {
    const updateData: ProfileUpdateData = {};

    if (firstName !== profile?.first_name) updateData.first_name = firstName;
    if (lastName !== profile?.last_name) updateData.last_name = lastName;
    if (email !== profile?.email) updateData.email = email;

    if (Object.keys(updateData).length === 0) {
      setIsEditing(false);
      return;
    }

    updateProfileMutation.mutate(updateData);
  };

  const handleChangePassword = () => {
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    changePasswordMutation.mutate({ password: newPassword });
  };

  const handleCancelEdit = () => {
    setFirstName(profile?.first_name || '');
    setLastName(profile?.last_name || '');
    setEmail(profile?.email || '');
    setIsEditing(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result as string;
        setUploadedImage(imageData);
        setSelectedAvatar(null);
        safeSetItem('userAvatarImage', imageData);
        safeRemoveItem('userAvatar');
        // Dispatch event to update header avatar
        window.dispatchEvent(new Event('avatarUpdated'));
        setShowAvatarModal(false);
        toast.success('Profile photo updated!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectBuiltInAvatar = (avatarId: string) => {
    setSelectedAvatar(avatarId);
    setUploadedImage(null);
    safeSetItem('userAvatar', avatarId);
    safeRemoveItem('userAvatarImage');
    // Dispatch event to update header avatar
    window.dispatchEvent(new Event('avatarUpdated'));
    setShowAvatarModal(false);
    toast.success('Avatar updated!');
  };

  const handle2FAToggle = (enabled: boolean) => {
    if (enabled) {
      setShow2FASetup(true);
    } else {
      // Show confirmation before disabling
      if (
        window.confirm(
          'Are you sure you want to disable two-factor authentication? This will make your account less secure.'
        )
      ) {
        setIs2FAEnabled(false);
        toast.success('Two-factor authentication disabled');
      }
    }
  };

  const handleVerify2FA = () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    // Simulate verification
    setIs2FAEnabled(true);
    setShow2FASetup(false);
    setVerificationCode('');
    toast.success('Two-factor authentication enabled!');
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800 ring-purple-600/20';
      case 'manager':
        return 'bg-blue-100 text-blue-800 ring-blue-600/20';
      case 'salesperson':
        return 'bg-green-100 text-green-800 ring-green-600/20';
      default:
        return 'bg-gray-100 text-gray-800 ring-gray-600/20';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-700 ring-green-600/20';
      case 'inactive':
        return 'bg-gray-50 text-gray-700 ring-gray-600/20';
      case 'suspended':
        return 'bg-red-50 text-red-700 ring-red-600/20';
      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20';
    }
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = [
    'bg-red-500',
    'bg-orange-500',
    'bg-yellow-500',
    'bg-blue-500',
    'bg-green-500',
  ];

  const renderAvatar = () => {
    if (uploadedImage) {
      return <img src={uploadedImage} alt="Profile" className="w-full h-full object-cover" />;
    }

    if (selectedAvatar) {
      const builtIn = BUILT_IN_AVATARS.find((a) => a.id === selectedAvatar);
      if (builtIn) {
        return (
          <div className={`w-full h-full flex items-center justify-center ${builtIn.bg}`}>
            <span className="text-4xl">{builtIn.emoji}</span>
          </div>
        );
      }
      const gradient = GRADIENT_AVATARS.find((a) => a.id === selectedAvatar);
      if (gradient) {
        return (
          <div className={`w-full h-full flex items-center justify-center ${gradient.bg}`}>
            <span className="text-3xl font-bold text-white">
              {profile?.first_name?.[0]}
              {profile?.last_name?.[0]}
            </span>
          </div>
        );
      }
    }

    // Default avatar with initials
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
        <span className="text-3xl font-bold text-white">
          {profile?.first_name?.[0]}
          {profile?.last_name?.[0]}
        </span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 animate-spin text-blue-500 mx-auto" />
          <p className="mt-4 text-gray-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <p className="text-red-500 mb-4">Failed to load profile</p>
        <button onClick={() => refetch()} className="btn-secondary">
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  const tabs = [
    { name: 'Profile', icon: UserCircleIcon },
    { name: 'Security', icon: ShieldCheckIcon },
    { name: 'Notifications', icon: BellIcon },
    { name: 'Preferences', icon: PaintBrushIcon },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Profile Header Card */}
      <div className="bg-white shadow-lg rounded-2xl overflow-hidden mb-6">
        <div className="relative h-32 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>

        <div className="relative px-6 pb-6">
          {/* Avatar Section */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-5">
            <div className="-mt-16 relative group">
              <div className="w-28 h-28 rounded-2xl ring-4 ring-white shadow-xl overflow-hidden bg-white">
                {renderAvatar()}
              </div>
              <button
                onClick={() => setShowAvatarModal(true)}
                aria-label="Change avatar"
                data-testid="change-avatar-button"
                className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"
              >
                <CameraIcon className="h-8 w-8 text-white" />
              </button>
              <div className="absolute -bottom-1 -right-1 bg-green-500 w-5 h-5 rounded-full border-2 border-white"></div>
            </div>

            <div className="mt-4 sm:mt-0 sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-between sm:space-x-6 sm:pb-1">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 truncate">
                  {profile?.first_name} {profile?.last_name}
                </h1>
                <p className="text-sm text-gray-500 flex items-center mt-1">
                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                  {profile?.email}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getRoleBadgeColor(profile?.role || '')}`}
                  >
                    {profile?.role?.charAt(0).toUpperCase()}
                    {profile?.role?.slice(1)}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${getStatusBadgeColor(profile?.status || '')}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current mr-1.5"></span>
                    {profile?.status?.charAt(0).toUpperCase()}
                    {profile?.status?.slice(1)}
                  </span>
                  {is2FAEnabled && (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                      <ShieldCheckIcon className="h-3 w-3 mr-1" />
                      2FA Enabled
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row sm:space-x-3">
                <button onClick={() => setActiveTab(0)} className="btn-primary">
                  <UserCircleIcon className="h-4 w-4 mr-2" />
                  Edit Profile
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Member Since</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {profile?.created_at
                  ? new Date(profile.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      year: 'numeric',
                    })
                  : '-'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Last Login</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {profile?.last_login_at
                  ? new Date(profile.last_login_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                  : 'Never'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Role</p>
              <p className="text-lg font-semibold text-gray-900 mt-1 capitalize">{profile?.role}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Security</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {is2FAEnabled ? '2FA Active' : 'Basic'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Content */}
      <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                classNames(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5 transition-all',
                  'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-blue-400 ring-white ring-opacity-60',
                  selected
                    ? 'bg-white text-blue-700 shadow'
                    : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
                )
              }
            >
              <div className="flex items-center justify-center">
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </div>
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          {/* Profile Tab */}
          <Tab.Panel>
            <div className="bg-white shadow rounded-xl">
              <div className="px-6 py-5 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                    <p className="text-sm text-gray-500">
                      Update your personal details and contact information
                    </p>
                  </div>
                  {!isEditing ? (
                    <button onClick={() => setIsEditing(true)} className="btn-secondary">
                      Edit
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCancelEdit}
                        className="btn-secondary"
                        disabled={updateProfileMutation.isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        className="btn-primary"
                        disabled={updateProfileMutation.isLoading}
                      >
                        {updateProfileMutation.isLoading ? (
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="form-input w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="First name"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{profile?.first_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="form-input w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Last name"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{profile?.last_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                      Email Address
                    </label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-input w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Email address"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{profile?.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <DevicePhoneMobileIcon className="h-4 w-4 inline mr-1" />
                      Phone Number
                    </label>
                    {isEditing ? (
                      <PhoneInput
                        value={phone}
                        onChange={setPhone}
                        className="form-input w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="(555) 123-4567"
                      />
                    ) : (
                      <p className="text-gray-900 py-2">{phone || 'Not set'}</p>
                    )}
                  </div>
                </div>

                {/* Account Info (Read-only) */}
                <div className="pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Account Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm text-gray-500">User ID</label>
                      <p className="text-sm font-mono text-gray-700 mt-1">
                        {profile?.id?.slice(0, 8)}...
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500">Role</label>
                      <p className="text-sm text-gray-900 mt-1 capitalize">{profile?.role}</p>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500">Account Status</label>
                      <p className="text-sm text-gray-900 mt-1 capitalize">{profile?.status}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Tab.Panel>

          {/* Security Tab */}
          <Tab.Panel>
            <div className="space-y-6">
              {/* Password Section */}
              <div className="bg-white shadow rounded-xl">
                <div className="px-6 py-5 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <KeyIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Password</h3>
                        <p className="text-sm text-gray-500">
                          Change your password regularly for better security
                        </p>
                      </div>
                    </div>
                    {!showPasswordSection && (
                      <button
                        onClick={() => setShowPasswordSection(true)}
                        className="btn-secondary"
                      >
                        Change Password
                      </button>
                    )}
                  </div>
                </div>

                {showPasswordSection && (
                  <div className="px-6 py-6">
                    <div className="max-w-md space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <PasswordInput
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="form-input w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500 pr-10"
                          placeholder="Enter new password"
                        />
                        {newPassword && (
                          <div className="mt-2">
                            <div className="flex space-x-1">
                              {[...Array(5)].map((_, i) => (
                                <div
                                  key={i}
                                  className={`h-1 flex-1 rounded ${i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'}`}
                                />
                              ))}
                            </div>
                            <p
                              className={`text-xs mt-1 ${passwordStrength >= 4 ? 'text-green-600' : passwordStrength >= 2 ? 'text-yellow-600' : 'text-red-600'}`}
                            >
                              {strengthLabels[passwordStrength - 1] || 'Enter a password'}
                            </p>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <PasswordInput
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="form-input w-full rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Confirm new password"
                        />
                        {confirmPassword && newPassword !== confirmPassword && (
                          <p className="text-xs text-red-600 mt-1">Passwords don't match</p>
                        )}
                      </div>

                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          onClick={() => {
                            setShowPasswordSection(false);
                            setNewPassword('');
                            setConfirmPassword('');
                          }}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleChangePassword}
                          className="btn-primary"
                          disabled={
                            changePasswordMutation.isLoading ||
                            !newPassword ||
                            newPassword !== confirmPassword
                          }
                        >
                          {changePasswordMutation.isLoading ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          ) : (
                            'Update Password'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Two-Factor Authentication */}
              <div className="bg-white shadow rounded-xl">
                <div className="px-6 py-5 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Two-Factor Authentication
                        </h3>
                        <p className="text-sm text-gray-500">
                          Add an extra layer of security to your account
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={is2FAEnabled}
                      onChange={handle2FAToggle}
                      className={`${is2FAEnabled ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${is2FAEnabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </Switch>
                  </div>
                </div>

                <div className="px-6 py-6">
                  {is2FAEnabled ? (
                    <div className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-800">
                          Two-factor authentication is enabled
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Your account is protected with{' '}
                          {twoFAMethod === 'sms' ? 'SMS verification' : 'an authenticator app'}.
                        </p>
                        <button className="text-sm text-blue-600 hover:text-blue-700 mt-2">
                          Manage backup codes →
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        onClick={() => {
                          setTwoFAMethod('authenticator');
                          setShow2FASetup(true);
                        }}
                        className="flex items-start p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200">
                          <KeyIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4 text-left">
                          <h4 className="text-sm font-medium text-gray-900">Authenticator App</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Use Google Authenticator, Authy, or similar apps
                          </p>
                        </div>
                      </button>

                      <button
                        onClick={() => {
                          setTwoFAMethod('sms');
                          setShow2FASetup(true);
                        }}
                        className="flex items-start p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200">
                          <DevicePhoneMobileIcon className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-4 text-left">
                          <h4 className="text-sm font-medium text-gray-900">SMS Verification</h4>
                          <p className="text-xs text-gray-500 mt-1">
                            Receive a code via text message
                          </p>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Active Sessions */}
              <div className="bg-white shadow rounded-xl">
                <div className="px-6 py-5 border-b border-gray-200">
                  <div className="flex items-center">
                    <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Active Sessions</h3>
                      <p className="text-sm text-gray-500">Manage your active login sessions</p>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Current Session</p>
                        <p className="text-xs text-gray-500">
                          Chrome on Linux • {new Date().toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-green-600 font-medium">Active now</span>
                  </div>
                </div>
              </div>
            </div>
          </Tab.Panel>

          {/* Notifications Tab */}
          <Tab.Panel>
            <div className="bg-white shadow rounded-xl">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Notification Preferences</h3>
                <p className="text-sm text-gray-500">Choose how you want to be notified</p>
              </div>

              <div className="px-6 py-6 space-y-6">
                {/* Notification Channels */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Notification Channels</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                          <p className="text-xs text-gray-500">Receive updates via email</p>
                        </div>
                      </div>
                      <Switch
                        checked={emailNotifications}
                        onChange={(checked) => {
                          setEmailNotifications(checked);
                          safeSetItem('emailNotifications', JSON.stringify(checked));
                        }}
                        className={`${emailNotifications ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                      >
                        <span
                          className={`${emailNotifications ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <DevicePhoneMobileIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
                          <p className="text-xs text-gray-500">Receive urgent alerts via SMS</p>
                        </div>
                      </div>
                      <Switch
                        checked={smsNotifications}
                        onChange={(checked) => {
                          setSmsNotifications(checked);
                          safeSetItem('smsNotifications', JSON.stringify(checked));
                        }}
                        className={`${smsNotifications ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                      >
                        <span
                          className={`${smsNotifications ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>
                  </div>
                </div>

                {/* Alert Types */}
                <div className="pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Alert Types</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Deal Alerts</p>
                        <p className="text-xs text-gray-500">
                          New deals, status changes, and approvals
                        </p>
                      </div>
                      <Switch
                        checked={dealAlerts}
                        onChange={(checked) => {
                          setDealAlerts(checked);
                          safeSetItem('dealAlerts', JSON.stringify(checked));
                        }}
                        className={`${dealAlerts ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                      >
                        <span
                          className={`${dealAlerts ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Inventory Alerts</p>
                        <p className="text-xs text-gray-500">
                          Low stock, new arrivals, and price changes
                        </p>
                      </div>
                      <Switch
                        checked={inventoryAlerts}
                        onChange={(checked) => {
                          setInventoryAlerts(checked);
                          safeSetItem('inventoryAlerts', JSON.stringify(checked));
                        }}
                        className={`${inventoryAlerts ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                      >
                        <span
                          className={`${inventoryAlerts ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Payment Reminders</p>
                        <p className="text-xs text-gray-500">Upcoming and overdue payments</p>
                      </div>
                      <Switch
                        checked={paymentReminders}
                        onChange={(checked) => {
                          setPaymentReminders(checked);
                          safeSetItem('paymentReminders', JSON.stringify(checked));
                        }}
                        className={`${paymentReminders ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out`}
                      >
                        <span
                          className={`${paymentReminders ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Tab.Panel>

          {/* Preferences Tab */}
          <Tab.Panel>
            <div className="bg-white dark:bg-gray-800 shadow rounded-xl">
              <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Display Preferences
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Customize your experience
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      // Save all preferences to localStorage
                      safeSetItem(
                        'preferences',
                        JSON.stringify({
                          timezone,
                          showCurrencySymbols,
                          use12HourFormat,
                          compactTableView,
                        })
                      );
                      toast.success('Preferences saved successfully!');
                    }}
                    className="btn-primary"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    Save Preferences
                  </button>
                </div>
              </div>

              <div className="px-6 py-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                    Timezone
                  </label>
                  <select
                    value={timezone}
                    onChange={(e) => {
                      setTimezone(e.target.value);
                      safeSetItem('timezone', e.target.value);
                    }}
                    className="form-select w-full max-w-md rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Theme</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Changes apply immediately
                      </p>
                    </div>
                    {selectedTheme === 'dark' && (
                      <span className="inline-flex items-center rounded-full bg-gray-800 px-2.5 py-0.5 text-xs font-medium text-white">
                        Dark Mode Active
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 max-w-md">
                    <button
                      onClick={() => {
                        setSelectedTheme('light');
                        toast.success('Light theme activated');
                      }}
                      className={`p-4 border-2 rounded-xl bg-white dark:bg-gray-700 text-center transition-all ${selectedTheme === 'light' ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
                    >
                      <div className="w-8 h-8 mx-auto bg-gray-100 rounded-lg mb-2"></div>
                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                        Light
                      </span>
                      {selectedTheme === 'light' && (
                        <div className="mt-1">
                          <CheckCircleIcon className="h-4 w-4 text-blue-600 mx-auto" />
                        </div>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTheme('dark');
                        toast.success('Dark theme activated');
                      }}
                      className={`p-4 border-2 rounded-xl bg-white dark:bg-gray-700 text-center transition-all ${selectedTheme === 'dark' ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
                    >
                      <div className="w-8 h-8 mx-auto bg-gray-800 rounded-lg mb-2"></div>
                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                        Dark
                      </span>
                      {selectedTheme === 'dark' && (
                        <div className="mt-1">
                          <CheckCircleIcon className="h-4 w-4 text-blue-600 mx-auto" />
                        </div>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTheme('system');
                        toast.success('System theme preference activated');
                      }}
                      className={`p-4 border-2 rounded-xl bg-white dark:bg-gray-700 text-center transition-all ${selectedTheme === 'system' ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
                    >
                      <div className="w-8 h-8 mx-auto bg-gradient-to-b from-gray-100 to-gray-800 rounded-lg mb-2"></div>
                      <span className="text-xs font-medium text-gray-900 dark:text-white">
                        System
                      </span>
                      {selectedTheme === 'system' && (
                        <div className="mt-1">
                          <CheckCircleIcon className="h-4 w-4 text-blue-600 mx-auto" />
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                    Data Display
                  </h4>
                  <div className="space-y-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox rounded text-blue-600 focus:ring-blue-500"
                        checked={showCurrencySymbols}
                        onChange={(e) => {
                          setShowCurrencySymbols(e.target.checked);
                          safeSetItem('showCurrencySymbols', JSON.stringify(e.target.checked));
                          toast.success(
                            e.target.checked
                              ? 'Currency symbols enabled'
                              : 'Currency symbols disabled'
                          );
                        }}
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Show currency symbols
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox rounded text-blue-600 focus:ring-blue-500"
                        checked={use12HourFormat}
                        onChange={(e) => {
                          setUse12HourFormat(e.target.checked);
                          safeSetItem('use12HourFormat', JSON.stringify(e.target.checked));
                          toast.success(
                            e.target.checked
                              ? '12-hour time format enabled'
                              : '24-hour time format enabled'
                          );
                        }}
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Use 12-hour time format
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox rounded text-blue-600 focus:ring-blue-500"
                        checked={compactTableView}
                        onChange={(e) => {
                          setCompactTableView(e.target.checked);
                          safeSetItem('compactTableView', JSON.stringify(e.target.checked));
                          toast.success(
                            e.target.checked
                              ? 'Compact table view enabled'
                              : 'Standard table view enabled'
                          );
                        }}
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Compact table view
                      </span>
                    </label>
                  </div>
                </div>

                {/* Theme Status Debug Info */}
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="bg-blue-50 dark:bg-gray-700 border border-blue-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <ShieldCheckIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Theme Status
                        </h3>
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-200">
                          <p>
                            Current theme: <span className="font-semibold">{selectedTheme}</span>
                          </p>
                          <p className="mt-1">
                            Dark mode class:{' '}
                            <span className="font-semibold">
                              {document.documentElement.classList.contains('dark')
                                ? 'Applied ✓'
                                : 'Not applied'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Avatar Selection Modal */}
      <Transition appear show={showAvatarModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowAvatarModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title as="div" className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Choose Your Avatar</h3>
                    <button
                      onClick={() => setShowAvatarModal(false)}
                      aria-label="Close avatar modal"
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </Dialog.Title>

                  {/* Upload Photo */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Upload Photo</h4>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all"
                    >
                      <CameraIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload a photo</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>

                  {/* Gradient Avatars */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Gradient Styles (with your initials)
                    </h4>
                    <div className="grid grid-cols-6 gap-3">
                      {GRADIENT_AVATARS.map((avatar) => (
                        <button
                          key={avatar.id}
                          onClick={() => handleSelectBuiltInAvatar(avatar.id)}
                          data-avatar-type="gradient"
                          aria-label={`Gradient avatar ${avatar.id}`}
                          className={`w-12 h-12 rounded-xl ${avatar.bg} flex items-center justify-center ring-2 ring-offset-2 transition-all ${selectedAvatar === avatar.id ? 'ring-blue-500' : 'ring-transparent hover:ring-gray-300'}`}
                        >
                          <span className="text-white font-bold text-sm">
                            {profile?.first_name?.[0]}
                            {profile?.last_name?.[0]}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Emoji Avatars */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Fun Avatars</h4>
                    <div className="grid grid-cols-6 gap-3">
                      {BUILT_IN_AVATARS.map((avatar) => (
                        <button
                          key={avatar.id}
                          onClick={() => handleSelectBuiltInAvatar(avatar.id)}
                          data-avatar-type="emoji"
                          aria-label={`Emoji avatar ${avatar.emoji}`}
                          className={`w-12 h-12 rounded-xl ${avatar.bg} flex items-center justify-center ring-2 ring-offset-2 transition-all ${selectedAvatar === avatar.id ? 'ring-blue-500' : 'ring-transparent hover:ring-gray-300'}`}
                        >
                          <span className="text-2xl">{avatar.emoji}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* 2FA Setup Modal */}
      <Transition appear show={show2FASetup} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShow2FASetup(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title as="div" className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {twoFAMethod === 'authenticator'
                        ? 'Set Up Authenticator App'
                        : 'Set Up SMS Verification'}
                    </h3>
                    <button
                      onClick={() => setShow2FASetup(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </Dialog.Title>

                  {twoFAMethod === 'authenticator' ? (
                    <div className="space-y-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-4">
                          Scan this QR code with your authenticator app (Google Authenticator,
                          Authy, etc.)
                        </p>
                        <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center">
                          {/* QR Code placeholder - in production, generate real QR */}
                          <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                            <div className="text-center">
                              <KeyIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-xs text-gray-500">QR Code</p>
                              <p className="text-xs text-gray-400 mt-1 font-mono">
                                JBSWY3DPEHPK3PXP
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter the 6-digit code from your app
                        </label>
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) =>
                            setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                          }
                          className="form-input w-full text-center text-2xl tracking-widest font-mono rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="000000"
                          maxLength={6}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <p className="text-sm text-gray-600 mb-4">
                          We'll send a verification code to your phone number
                        </p>
                        <div className="flex items-center space-x-2">
                          <PhoneInput
                            value={phone}
                            onChange={setPhone}
                            className="form-input flex-1 rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="(555) 123-4567"
                          />
                          <button className="btn-secondary">Send Code</button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter the 6-digit code from SMS
                        </label>
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) =>
                            setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                          }
                          className="form-input w-full text-center text-2xl tracking-widest font-mono rounded-lg border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="000000"
                          maxLength={6}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end space-x-3">
                    <button onClick={() => setShow2FASetup(false)} className="btn-secondary">
                      Cancel
                    </button>
                    <button
                      onClick={handleVerify2FA}
                      className="btn-primary"
                      disabled={verificationCode.length !== 6}
                    >
                      Verify & Enable
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default ProfilePage;
